/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Heading, Text } from '@towersystems/towersystems-design-system';
import { get } from 'lodash';

/**
 * Internal dependencies
 */
import Card from 'components/card';
import AnnotatedSection from 'components/annotated-section';
import FormInputCheckbox from 'components/forms/form-checkbox';
import FormTextInput from 'components/forms/form-text-input';
import FormNumberInput from 'components/forms/form-number-input';
import FormLabel from 'components/forms/form-label';

const FinditPreferences = ({ saveConfigValue, saveConfigValueInput, values }) => {
    return (
        <Stack space='xlarge' dividers>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Product'
                    description='Choose your preferences when sending product data.'
                />
                <AnnotatedSection.Content>
                    <Stack space='xlarge'>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product title'
                                    onChange={saveConfigValue}
                                    name='updateProductName'
                                    checked={get(values, 'updateProductName', false)}
                                >
                                    When ticked the Description from Retailer will update 
                                    Product Name/Title in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product description'
                                    onChange={saveConfigValue}
                                    name='updateProductDescription'
                                    checked={get(values, 'updateProductDescription', false)}
                                >
                                    When ticked the Web Description from Retailer will be 
                                    used to update Description in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product attributes'
                                    onChange={saveConfigValue}
                                    name='updateProductAttributes'
                                    checked={get(values, 'updateProductAttributes', false)}
                                >
                                    When ticked, attributes from Retailer will be used to update attributes.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product weight'
                                    onChange={saveConfigValue}
                                    name='updateProductWeight'
                                    checked={get(values, 'updateProductWeight', false)}
                                >
                                    When ticked the product weight and unit field from Retailer will 
                                    update the product weight filed in your ecommerce platform. 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product dimensions'
                                    onChange={saveConfigValue}
                                    name='updateProductDimensions'
                                    checked={get(values, 'updateProductDimensions', false)}
                                >
                                    When ticked the product dimensions from Retailer attribute will 
                                    update the product shipping dimensions in your ecommerce platform. 
                                    <br /><br />
                                    PLEASE NOTE: The dimensions in your Retailer classifications must be  
                                    named as LENGTH, WIDTH and HEIGHT.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product price'
                                    onChange={saveConfigValue}
                                    name='updateProductPrice'
                                    checked={get(values, 'updateProductPrice', false)}
                                >
                                    When ticked the chosen price from Retailer (see inventory and pricing below) 
                                    will be used to update the product price in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                    </Stack>
                </AnnotatedSection.Content>
            </AnnotatedSection>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Inventory & Pricing'
                    description='Choose your preferences when sending inventory and pricing data.'
                />
                <AnnotatedSection.Content>
                    <Stack space='large'>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product inventory levels'
                                    onChange={saveConfigValue}
                                    name='updateInventory'
                                    checked={get(values, 'updateInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be 
                                    used as the inventory level in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='TWC Track product inventory levels'
                                    onChange={saveConfigValue}
                                    name='trackInventory'
                                    checked={get(values, 'trackInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be subtracted 
                                    by the reported, but not yet imported, TWC sales 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Round down QOH To Whole Number'
                                    onChange={saveConfigValue}
                                    name='roundDownInventory'
                                    checked={get(values, 'roundDownInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be 
                                    rounded down in your ecommerce platform if they are in decimal
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use web price'
                                    onChange={saveConfigValue}
                                    name='useWebPrice'
                                    checked={get(values, 'useWebPrice', false)}
                                >
                                    When ticked the Retailer Web Price will be used as the sell 
                                    price of the item.  If unticked the system will use the Retailer Retail Price.  
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                        <Card>
                            <Card.Section>
                                <Box className="card__section-outofstockqty">
                                    <FormLabel>Out of Stock Quantity:</FormLabel>
                                    <FormNumberInput name='outOfStockQty' onChange={saveConfigValueInput} value={get(values, 'outOfStockQty')} />
                                    <Text className="form-text-input__additional-content">The Quantity entered here will be used as out of stock quantity. 
                                    Any qty below this value will be marked as out of stock on your ecommerce platform.</Text>
                                </Box>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Unpublish out of stock'
                                    onChange={saveConfigValue}
                                    name='unpublishOutOfStock'
                                    checked={get(values, 'unpublishOutOfStock', false)}
                                >
                                    When ticked, products with chosen inventory will be unpublished or 
                                    unavailable for purchase on your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                    </Stack>
                </AnnotatedSection.Content>
            </AnnotatedSection>
        </Stack>
    );
};

export default connect((state, ownProps) => ({}), {})(FinditPreferences);
